var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/de';
import utcPlugin from 'dayjs/plugin/utc';
import advancedFormatPlugin from 'dayjs/plugin/advancedFormat';
import localizedFormatPlugin from 'dayjs/plugin/localizedFormat';
import customParseFormatPlugin from 'dayjs/plugin/customParseFormat';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
dayjs.extend(utcPlugin);
dayjs.extend(advancedFormatPlugin);
dayjs.extend(localizedFormatPlugin);
dayjs.extend(customParseFormatPlugin);
dayjs.extend(relativeTimePlugin);
var YEAR_FIRST_FORMATS = [
    'YYYY-MM-DD',
    'YYYY-M-D',
    'YYYY/MM/DD',
    'YYYY/M/D',
    'YYYY.MM.DD.',
    'YYYY.MM.DD',
    'YYYY.M.D.',
    'YYYY.M.D',
];
/**
 * Formats Date into string with given format (uses UTC time)
 * @param date native JS Date
 * @param format `dayjs` date format
 * @returns formatted date
 */
export function formatDateWithFormat(date, format, useLocalTime) {
    if (useLocalTime === void 0) { useLocalTime = false; }
    if (useLocalTime) {
        return dayjs(date).format(format);
    }
    else {
        return dayjs.utc(date).format(format);
    }
}
/**
 * Formats Date into string according to locale (uses UTC time)
 * @param date native JS Date
 * @param format `dayjs` date format
 * @returns formatted date
 */
export function formatDateWithLocale(date, locale) {
    return dayjs.utc(date).locale(locale).format('L');
}
export function dateDifference(dateA, dateB, unit) {
    if (unit === void 0) { unit = 'days'; }
    return dayjs(dateA).diff(dateB, unit);
}
/**
 * Parses string as JS Date (with correct UTC time)
 * @param date string with date
 * @param formats formats to use for trying to parse date
 * @param includeYearFirstFormats adds formats YYYY-MM-DD, YYYY/MM/DD ...
 * @returns native JS Date object
 */
export function parseDateWithFormat(date, formats, includeYearFirstFormats) {
    var e_1, _a;
    if (includeYearFirstFormats === void 0) { includeYearFirstFormats = true; }
    var parseFormats = __spreadArray([], __read(formats), false);
    if (includeYearFirstFormats) {
        parseFormats.push.apply(parseFormats, __spreadArray([], __read(YEAR_FIRST_FORMATS), false));
    }
    try {
        for (var parseFormats_1 = __values(parseFormats), parseFormats_1_1 = parseFormats_1.next(); !parseFormats_1_1.done; parseFormats_1_1 = parseFormats_1.next()) {
            var parseFormat = parseFormats_1_1.value;
            try {
                var parsedDate = dayjs.utc(date, parseFormat).toDate();
                if (!Number.isNaN(parsedDate.getTime())) {
                    // valid Date (Invalid Date wil return NaN from getTime())
                    return parsedDate;
                }
            }
            catch (err) { }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (parseFormats_1_1 && !parseFormats_1_1.done && (_a = parseFormats_1.return)) _a.call(parseFormats_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return null;
}
/**
 * Parses string as JS Date (with correct UTC time)
 * @param date string with date
 * @param locale locale to use for parsing formats
 * @param includeYearFirstFormats adds formats YYYY-MM-DD, YYYY/MM/DD ...
 * @returns native JS Date object
 */
export function parseDateWithLocale(date, locale, includeYearFirstFormats) {
    var e_2, _a;
    if (includeYearFirstFormats === void 0) { includeYearFirstFormats = true; }
    var parseFormats = [];
    var localeFormats = getLocaleDateFormats(locale);
    var standardLocaleFormat = localeFormats.L;
    if (standardLocaleFormat !== undefined) {
        // add format with no leading zeroes for days and months
        var hasLongMonth = /MM/.test(standardLocaleFormat);
        var hasLongDay = /DD/.test(standardLocaleFormat);
        if (hasLongDay && hasLongMonth) {
            parseFormats.push(standardLocaleFormat.replace(/DD/, 'D').replace(/MM/, 'M')); // gives "D.M.YYYY"
        }
        // add standard locale format (with leading zeroes)
        parseFormats.push(standardLocaleFormat);
    }
    if (includeYearFirstFormats) {
        parseFormats.push.apply(parseFormats, __spreadArray([], __read(YEAR_FIRST_FORMATS), false));
    }
    try {
        for (var parseFormats_2 = __values(parseFormats), parseFormats_2_1 = parseFormats_2.next(); !parseFormats_2_1.done; parseFormats_2_1 = parseFormats_2.next()) {
            var parseFormat = parseFormats_2_1.value;
            try {
                var parsedDate = dayjs.utc(date, parseFormat).toDate();
                if (!Number.isNaN(parsedDate.getTime())) {
                    // valid Date (Invalid Date wil return NaN from getTime())
                    return parsedDate;
                }
            }
            catch (err) { }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (parseFormats_2_1 && !parseFormats_2_1.done && (_a = parseFormats_2.return)) _a.call(parseFormats_2);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return null;
}
/**
 * Fetches `dayjs` date formats for locale
 * @returns L, LL, LLL, LLLL, LT, LTS formats for `locale`
 */
export function getLocaleDateFormats(locale) {
    var _a;
    var localeData = dayjs.Ls[locale];
    return (_a = localeData === null || localeData === void 0 ? void 0 : localeData.formats) !== null && _a !== void 0 ? _a : {};
}
